import { supabase } from '../lib/supabase';

// Analytics Service - Advanced portfolio analytics and ROI tracking
export class AnalyticsService {
  
  // Get portfolio overview with ROI and performance metrics
  static async getPortfolioAnalytics(userId) {
    try {
      // Get all collections with metrics
      const { data: collections, error: collectionsError } = await supabase
        ?.from('collections')
        ?.select(`
          *,
          collection_metrics(*),
          collectibles(id, purchase_price, current_value, name, collectible_type)
        `)
        ?.eq('user_id', userId);

      if (collectionsError) throw collectionsError;

      // Calculate portfolio-level metrics
      const totalCost = collections?.reduce((sum, col) => 
        sum + (col?.collectibles?.reduce((s, item) => s + (parseFloat(item?.purchase_price) || 0), 0) || 0), 0);
      
      const totalValue = collections?.reduce((sum, col) => 
        sum + (col?.collectibles?.reduce((s, item) => s + (parseFloat(item?.current_value) || 0), 0) || 0), 0);
      
      const totalItems = collections?.reduce((sum, col) => sum + (col?.collectibles?.length || 0), 0);
      
      const portfolioROI = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
      const unrealizedGains = totalValue - totalCost;

      return {
        data: {
          totalCost,
          totalValue,
          totalItems,
          portfolioROI,
          unrealizedGains,
          collections: collections?.map(col => ({
            ...col,
            roi: col?.collection_metrics?.[0]?.roi_percentage || 0,
            cost: col?.collection_metrics?.[0]?.total_cost || 0,
            value: col?.collection_metrics?.[0]?.total_current_value || 0
          }))
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching portfolio analytics:', error);
      return { data: null, error };
    }
  }

  // Get price history for trend analysis
  static async getPriceHistory(collectibleId, timeRange = '1Y') {
    try {
      const dateRanges = {
        '1M': 30,
        '3M': 90,
        '6M': 180,
        '1Y': 365,
        'All': 3650
      };

      const daysBack = dateRanges?.[timeRange] || 365;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysBack);

      const { data, error } = await supabase
        ?.from('price_history')
        ?.select('*')
        ?.eq('collectible_id', collectibleId)
        ?.gte('recorded_at', startDate?.toISOString())
        ?.order('recorded_at', { ascending: true });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching price history:', error);
      return { data: [], error };
    }
  }

  // Get portfolio snapshots for performance tracking
  static async getPortfolioSnapshots(userId, timeRange = '1Y') {
    try {
      const dateRanges = {
        '1M': 30,
        '3M': 90,
        '6M': 180,
        '1Y': 365,
        'All': 3650
      };

      const daysBack = dateRanges?.[timeRange] || 365;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysBack);

      const { data, error } = await supabase
        ?.from('portfolio_snapshots')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.gte('snapshot_date', startDate?.toISOString()?.split('T')?.[0])
        ?.order('snapshot_date', { ascending: true });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching portfolio snapshots:', error);
      return { data: [], error };
    }
  }

  // Create portfolio snapshot (for daily tracking)
  static async createPortfolioSnapshot(userId) {
    try {
      const analytics = await this.getPortfolioAnalytics(userId);
      
      if (analytics?.error || !analytics?.data) {
        throw new Error('Failed to get portfolio analytics');
      }

      const { totalCost, totalValue, totalItems, portfolioROI, collections } = analytics?.data;

      const { data, error } = await supabase
        ?.from('portfolio_snapshots')
        ?.insert([{
          user_id: userId,
          total_value: totalValue,
          total_cost: totalCost,
          total_items: totalItems,
          roi_percentage: portfolioROI,
          snapshot_date: new Date()?.toISOString()?.split('T')?.[0],
          metadata: {
            collections: collections?.map(col => ({
              id: col?.id,
              name: col?.name,
              value: col?.value,
              roi: col?.roi
            }))
          }
        }])
        ?.select()
        ?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating portfolio snapshot:', error);
      return { data: null, error };
    }
  }

  // Get collection comparison data
  static async getCollectionComparison(userId) {
    try {
      const { data: collections, error } = await supabase
        ?.from('collections')
        ?.select(`
          *,
          collection_metrics(*),
          collectibles(id, purchase_price, current_value, name)
        `)
        ?.eq('user_id', userId);

      if (error) throw error;

      const comparison = collections?.map(col => {
        const metrics = col?.collection_metrics?.[0];
        const items = col?.collectibles || [];
        
        return {
          id: col?.id,
          name: col?.name,
          type: col?.collectible_type,
          itemCount: items?.length,
          totalCost: metrics?.total_cost || 0,
          totalValue: metrics?.total_current_value || 0,
          roi: metrics?.roi_percentage || 0,
          avgItemValue: metrics?.avg_item_value || 0,
          unrealizedGains: (metrics?.total_current_value || 0) - (metrics?.total_cost || 0)
        };
      });

      return { data: comparison || [], error: null };
    } catch (error) {
      console.error('Error fetching collection comparison:', error);
      return { data: [], error };
    }
  }

  // Get top performers across portfolio
  static async getTopPerformers(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        ?.from('collectibles')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.not('purchase_price', 'is', null)
        ?.gt('purchase_price', 0)
        ?.order('current_value', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      const performers = data?.map(item => ({
        ...item,
        roi: ((parseFloat(item?.current_value) - parseFloat(item?.purchase_price)) / parseFloat(item?.purchase_price)) * 100,
        gain: parseFloat(item?.current_value) - parseFloat(item?.purchase_price)
      }));

      // Sort by ROI
      performers?.sort((a, b) => b?.roi - a?.roi);

      return { data: performers || [], error: null };
    } catch (error) {
      console.error('Error fetching top performers:', error);
      return { data: [], error };
    }
  }

  // Generate portfolio trend data for charts
  static async getPortfolioTrends(userId, timeRange = '1Y') {
    try {
      // Try to get snapshots first
      const snapshotsResult = await this.getPortfolioSnapshots(userId, timeRange);
      
      if (snapshotsResult?.data?.length > 0) {
        return {
          data: snapshotsResult?.data?.map(snapshot => ({
            date: snapshot?.snapshot_date,
            value: parseFloat(snapshot?.total_value),
            cost: parseFloat(snapshot?.total_cost),
            roi: parseFloat(snapshot?.roi_percentage)
          })),
          error: null
        };
      }

      // If no snapshots, generate mock data based on current values
      const analytics = await this.getPortfolioAnalytics(userId);
      
      if (analytics?.error || !analytics?.data) {
        return { data: [], error: analytics?.error };
      }

      const { totalValue, totalCost } = analytics?.data;
      const months = this.getMonthsArray(timeRange);
      
      const trendData = months?.map((month, index) => {
        const progress = index / (months?.length - 1);
        const value = totalCost + ((totalValue - totalCost) * progress);
        const cost = totalCost;
        const roi = cost > 0 ? ((value - cost) / cost) * 100 : 0;
        
        return {
          date: month,
          value: Math.round(value),
          cost: Math.round(cost),
          roi: parseFloat(roi?.toFixed(2))
        };
      });

      return { data: trendData, error: null };
    } catch (error) {
      console.error('Error generating portfolio trends:', error);
      return { data: [], error };
    }
  }

  // Helper to get months array based on time range
  static getMonthsArray(timeRange) {
    const monthCounts = {
      '1M': 4,
      '3M': 12,
      '6M': 24,
      '1Y': 12,
      'All': 12
    };

    const count = monthCounts?.[timeRange] || 12;
    const months = [];
    const now = new Date();

    for (let i = count - 1; i >= 0; i--) {
      const date = new Date(now);
      date?.setMonth(date?.getMonth() - i);
      months?.push(date?.toISOString()?.split('T')?.[0]);
    }

    return months;
  }

  // Export portfolio data to CSV
  static async exportPortfolioCSV(userId) {
    try {
      const { data: collections, error } = await supabase
        ?.from('collections')
        ?.select(`
          *,
          collection_metrics(*),
          collectibles(*)
        `)
        ?.eq('user_id', userId);

      if (error) throw error;

      const headers = [
        'Collection',
        'Item Name',
        'Type',
        'Condition',
        'Purchase Price',
        'Current Value',
        'ROI %',
        'Gain/Loss',
        'Purchase Date'
      ];

      const rows = [];
      
      collections?.forEach(collection => {
        collection?.collectibles?.forEach(item => {
          const purchasePrice = parseFloat(item?.purchase_price) || 0;
          const currentValue = parseFloat(item?.current_value) || 0;
          const roi = purchasePrice > 0 ? ((currentValue - purchasePrice) / purchasePrice) * 100 : 0;
          const gain = currentValue - purchasePrice;

          rows?.push([
            collection?.name,
            item?.name,
            item?.collectible_type,
            item?.condition,
            purchasePrice?.toFixed(2),
            currentValue?.toFixed(2),
            roi?.toFixed(2),
            gain?.toFixed(2),
            item?.created_at?.split('T')?.[0]
          ]);
        });
      });

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.join(','))
      ]?.join('\n');

      return { data: csvContent, error: null };
    } catch (error) {
      console.error('Error exporting portfolio CSV:', error);
      return { data: null, error };
    }
  }
}

export default AnalyticsService;